import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2, Play, Layers, Hash, ListFilter } from 'lucide-react';
import { Text, Badge, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Card, VStack, HStack, Grid, GridItem } from '@components/base/layout';
import { Tabs, Breadcrumb } from '@components/base/navigation';
import { Alert, Modal, Spinner, useToast } from '@components/base/feedback';
import { workflowDefinitionsService } from '../../api/workflowDefinitionsService';
import { workflowExecutionsService } from '../../api/workflowExecutionsService';
import { WorkflowStepsListPanel } from './WorkflowStepsListPanel';
import { WorkflowRulesPanel } from './WorkflowRulesPanel';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function DetailField({ label, children }: Readonly<{ label: string; children: React.ReactNode }>) {
  return (
    <VStack gap={1}>
      <Text size="sm" color="muted" weight="medium">
        {label}
      </Text>
      <div>{children}</div>
    </VStack>
  );
}

// ---------------------------------------------------------------------------
// Test Workflow Card
// ---------------------------------------------------------------------------

function TestWorkflowCard({ definitionId }: Readonly<{ definitionId: string }>) {
  const [contextData, setContextData] = useState('{}');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  const runMutation = useMutation({
    mutationFn: (parsedData: Record<string, unknown>) =>
      workflowExecutionsService.create({
        workflow_definition_id: definitionId,
        context_data: Object.keys(parsedData).length > 0 ? parsedData : undefined,
      }),
    onSuccess: (response) => {
      toast({ variant: 'success', title: 'Workflow execution started.' });
      navigate(`/admin/workflows/executions/${response.data.id}`);
    },
    onError: () => {
      toast({ variant: 'error', title: 'Failed to start workflow execution.' });
    },
  });

  const handleRun = () => {
    setJsonError(null);
    try {
      const parsed = JSON.parse(contextData) as Record<string, unknown>;
      runMutation.mutate(parsed);
    } catch {
      setJsonError('Invalid JSON. Please check your input.');
    }
  };

  return (
    <Card>
      <Card.Header title="Test Workflow" />
      <Card.Content>
        <VStack gap={4}>
          <VStack gap={1}>
            <Text size="sm" color="muted" weight="medium">
              Context Data (JSON)
            </Text>
            <textarea
              value={contextData}
              onChange={(e) => { setContextData(e.target.value); }}
              rows={6}
              className="w-full rounded-md border border-gray-300 bg-white text-gray-900 dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700 p-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder='{ "key": "value" }'
            />
          </VStack>
          {jsonError && (
            <Alert variant="error" title="Invalid JSON">
              {jsonError}
            </Alert>
          )}
          <HStack justify="end">
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Icon icon={Play} size="xs" color="inherit" />}
              onClick={handleRun}
              loading={runMutation.isPending}
            >
              Run
            </Button>
          </HStack>
        </VStack>
      </Card.Content>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Overview Tab
// ---------------------------------------------------------------------------

function OverviewTab({ slug }: Readonly<{ slug: string }>) {
  const { data: definitionResponse } = useQuery({
    queryKey: ['admin', 'workflows', slug],
    queryFn: () => workflowDefinitionsService.get(slug),
  });

  const definition = definitionResponse?.data;

  /* v8 ignore start */
  if (!definition) return null;
  /* v8 ignore stop */

  return (
    <Card>
      <Card.Header title="Definition Details" />
      <Card.Content>
        <Grid columns={2} gap={4}>
          <GridItem>
            <DetailField label="Slug">
              <Text>{definition.slug}</Text>
            </DetailField>
          </GridItem>
          <GridItem>
            <DetailField label="Name">
              <Text>{definition.name}</Text>
            </DetailField>
          </GridItem>
          <GridItem span={2}>
            <DetailField label="Description">
              <Text>{definition.description ?? '\u2014'}</Text>
            </DetailField>
          </GridItem>
          <GridItem>
            <DetailField label="Version">
              <Badge variant="info" size="sm">
                v{definition.version}
              </Badge>
            </DetailField>
          </GridItem>
          <GridItem>
            <DetailField label="Status">
              <Badge
                variant={definition.is_active ? 'success' : 'default'}
                size="sm"
              >
                {definition.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </DetailField>
          </GridItem>
          <GridItem span={2}>
            <DetailField label="Config">
              <Text size="sm" color="muted">
                {definition.config && Object.keys(definition.config).length > 0
                  ? JSON.stringify(definition.config, null, 2)
                  : '\u2014'}
              </Text>
            </DetailField>
          </GridItem>
          <GridItem>
            <DetailField label="Created">
              <Text size="sm">{new Date(definition.created_at).toLocaleString()}</Text>
            </DetailField>
          </GridItem>
          <GridItem>
            <DetailField label="Updated">
              <Text size="sm">{new Date(definition.updated_at).toLocaleString()}</Text>
            </DetailField>
          </GridItem>
        </Grid>
      </Card.Content>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export function WorkflowDefinitionDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  /* v8 ignore start */
  const slugOrEmpty = slug ?? '';
  /* v8 ignore stop */

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // ---- Fetch definition data ------------------------------------------------

  const {
    data: definitionResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['admin', 'workflows', slug],
    queryFn: () => {
      /* v8 ignore start */
      if (!slug) return Promise.reject(new Error('Workflow definition slug is required'));
      /* v8 ignore stop */
      return workflowDefinitionsService.get(slug);
    },
    enabled: !!slug,
  });

  const definition = definitionResponse?.data;

  // ---- Delete mutation ------------------------------------------------------

  const deleteMutation = useMutation({
    mutationFn: () => workflowDefinitionsService.remove(slugOrEmpty),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'workflows'] });
      toast({ variant: 'success', title: 'Workflow deleted successfully.' });
      navigate('/admin/workflows');
    },
    onError: () => {
      toast({ variant: 'error', title: 'Failed to delete workflow.' });
    },
  });

  // ---- Render: loading state ------------------------------------------------

  if (isLoading) {
    return (
      <VStack gap={6} align="center" className="py-12">
        <Spinner size="lg" />
        <Text color="muted">Loading workflow definition...</Text>
      </VStack>
    );
  }

  // ---- Render: error state --------------------------------------------------

  if (error || !definition) {
    return (
      <VStack gap={6}>
        <Breadcrumb>
          <Breadcrumb.Item href="/admin">Admin</Breadcrumb.Item>
          <Breadcrumb.Item href="/admin/workflows">Workflows</Breadcrumb.Item>
          <Breadcrumb.Item isCurrent>Details</Breadcrumb.Item>
        </Breadcrumb>
        <Alert variant="error" title="Failed to load workflow definition">
          {error instanceof Error
            ? error.message
            : 'Workflow definition not found or an unexpected error occurred.'}
        </Alert>
      </VStack>
    );
  }

  // ---- Render: main ---------------------------------------------------------

  return (
    <VStack gap={6}>
      {/* Breadcrumb navigation */}
      <Breadcrumb>
        <Breadcrumb.Item href="/admin">Admin</Breadcrumb.Item>
        <Breadcrumb.Item href="/admin/workflows">Workflows</Breadcrumb.Item>
        <Breadcrumb.Item isCurrent>{definition.name}</Breadcrumb.Item>
      </Breadcrumb>

      {/* Page header */}
      <HStack justify="between" align="start">
        <VStack gap={2}>
          <HStack gap={3} align="center">
            <Text as="h1" size="2xl" weight="bold">
              {definition.name}
            </Text>
            <Badge
              variant={definition.is_active ? 'success' : 'default'}
              size="sm"
            >
              {definition.is_active ? 'Active' : 'Inactive'}
            </Badge>
            <Badge variant="info" size="sm">
              v{definition.version}
            </Badge>
          </HStack>
          {definition.description && (
            <Text size="sm" color="muted">{definition.description}</Text>
          )}
        </VStack>
        <HStack gap={2}>
          <Button
            variant="secondary"
            leftIcon={<Icon icon={Pencil} size="sm" />}
            onClick={() => { navigate(`/admin/workflows/${slugOrEmpty}/edit`); }}
          >
            Edit
          </Button>
          <Button
            variant="danger"
            leftIcon={<Icon icon={Trash2} size="sm" />}
            onClick={() => { setShowDeleteModal(true); }}
          >
            Delete
          </Button>
        </HStack>
      </HStack>

      {/* Stats cards */}
      <HStack gap={4}>
        <Card padding="sm">
          <Card.Content>
            <HStack gap={2} align="center">
              <Icon icon={Layers} size="sm" color="primary" />
              <VStack gap={0}>
                <Text size="xs" color="muted">Steps</Text>
                <Text weight="bold">{definition.steps_count ?? 0}</Text>
              </VStack>
            </HStack>
          </Card.Content>
        </Card>
        <Card padding="sm">
          <Card.Content>
            <HStack gap={2} align="center">
              <Icon icon={Hash} size="sm" color="primary" />
              <VStack gap={0}>
                <Text size="xs" color="muted">Executions</Text>
                <Text weight="bold">{definition.executions_count ?? 0}</Text>
              </VStack>
            </HStack>
          </Card.Content>
        </Card>
        <Card padding="sm">
          <Card.Content>
            <HStack gap={2} align="center">
              <Icon icon={ListFilter} size="sm" color="primary" />
              <VStack gap={0}>
                <Text size="xs" color="muted">Rules</Text>
                <Text weight="bold">{definition.rules_count ?? 0}</Text>
              </VStack>
            </HStack>
          </Card.Content>
        </Card>
      </HStack>

      {/* Tabbed content */}
      <Tabs defaultValue="overview">
        <Tabs.List>
          <Tabs.Tab value="overview">Overview</Tabs.Tab>
          <Tabs.Tab value="steps">Steps</Tabs.Tab>
          <Tabs.Tab value="rules">Rules</Tabs.Tab>
          <Tabs.Tab value="test">Test</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overview">
          <OverviewTab slug={slugOrEmpty} />
        </Tabs.Panel>

        <Tabs.Panel value="steps">
          <WorkflowStepsListPanel workflowSlug={slugOrEmpty} />
        </Tabs.Panel>

        <Tabs.Panel value="rules">
          <WorkflowRulesPanel workflowSlug={slugOrEmpty} />
        </Tabs.Panel>

        <Tabs.Panel value="test">
          <TestWorkflowCard definitionId={definition.id} />
        </Tabs.Panel>
      </Tabs>

      {/* Delete confirmation modal */}
      <Modal
        open={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); }}
        size="sm"
      >
        <Modal.Header title="Delete Workflow" />
        <Modal.Body>
          <VStack gap={3}>
            <Text>
              Are you sure you want to delete the workflow{' '}
              <Text as="span" weight="bold">
                {definition.name}
              </Text>
              ? This action cannot be undone.
            </Text>
            {deleteMutation.isError && (
              <Alert variant="error" title="Delete failed">
                An unexpected error occurred.
              </Alert>
            )}
          </VStack>
        </Modal.Body>
        <Modal.Footer>
          <HStack gap={2} justify="end">
            <Button
              variant="secondary"
              onClick={() => { setShowDeleteModal(false); }}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={deleteMutation.isPending}
              onClick={() => { deleteMutation.mutate(); }}
            >
              Delete
            </Button>
          </HStack>
        </Modal.Footer>
      </Modal>
    </VStack>
  );
}
